/**
 * Base class for all O2D elements
 * Provides common functionality and event handling
 */

import {LitElement} from 'lit'

/**
 * Custom event detail type
 */
export type O2DEventDetail<TPayload> = {
    timestamp?: number;
} & TPayload;

export type O2DCustomEvent<TPayload> = CustomEvent<O2DEventDetail<TPayload>>;

/**
 * Abstract base class for O2D elements
 */
export abstract class AbstractO2dElement extends LitElement {

    /**
     * Opt-in switch for Shadow DOM. The default is `false`: the element renders into
     * its own light DOM, so global CSS reaches it directly (the behaviour of every
     * element that existed before this switch).
     *
     * A subclass sets `static useShadowDom = true` to render into an open shadow
     * root instead. It then ships its CSS through `static styles` and exposes its
     * look through custom properties and `part` attributes only; selectors from
     * outside no longer reach its inner nodes.
     *
     * Read per class at render-root creation, so a subclass of a shadow element can
     * switch it off again (and vice versa). Such a light-DOM subclass gets none of the
     * parent's packaged styles: `static styles` only apply inside a shadow root, so its
     * look has to come from document CSS.
     */
    static useShadowDom: boolean = false;

    /**
     * Creates the render root: an open shadow root when the class opted in via
     * {@link AbstractO2dElement.useShadowDom}, otherwise the element itself (light DOM).
     *
     * @return {HTMLElement | DocumentFragment} The element or document fragment to be used as the render root.
     */
    protected createRenderRoot(): HTMLElement | DocumentFragment {
        const useShadowDom = (this.constructor as typeof AbstractO2dElement).useShadowDom;
        return useShadowDom ? super.createRenderRoot() : this;
    }

    /**
     * Fire a custom event with detail
     */
    public fireEvent<TPayload>(
        name: string,
        detail?: TPayload,
        options?: EventInit
    ): boolean {
        const event: O2DCustomEvent<TPayload> = new CustomEvent(name, {
            bubbles: true,
            composed: true,
            ...options,
            detail: {
                timestamp: Date.now(),
                ...detail
            } as O2DEventDetail<TPayload & { timestamp: number }>
        });
        return this.dispatchEvent(event);
    }

    /**
     * Throttle function execution
     */
    protected throttle<T extends (...args: any[]) => any>(
        func: T,
        wait: number
    ): T {
        let inThrottle: boolean = false
        let lastResult: any

        return ((...args: Parameters<T>) => {
            if (!inThrottle) {
                lastResult = func.apply(this, args)
                inThrottle = true
                setTimeout(() => {
                    inThrottle = false
                }, wait)
            }
            return lastResult
        }) as T
    }

    /**
     * Debounce function execution
     */
    protected debounce<T extends (...args: any[]) => any>(
        func: T,
        wait: number
    ): T {
        let timeout: number | undefined

        return ((...args: Parameters<T>) => {
            const later = () => {
                timeout = undefined
                func.apply(this, args)
            }
            clearTimeout(timeout)
            timeout = window.setTimeout(later, wait)
        }) as T
    }

    /**
     * Get computed style property value
     */
    protected getStyleProperty(property: string): string {
        return getComputedStyle(this).getPropertyValue(property)
    }

    /**
     * Set CSS custom property
     */
    protected setStyleProperty(property: string, value: string): void {
        this.style.setProperty(property, value)
    }

    /**
     * Request animation frame with cleanup
     */
    private rafId?: number

    protected requestFrame(callback: FrameRequestCallback): void {
        if (this.rafId !== undefined) {
            cancelAnimationFrame(this.rafId)
        }
        this.rafId = requestAnimationFrame(callback)
    }

    /**
     * Cleanup on disconnect
     */
    override disconnectedCallback(): void {
        super.disconnectedCallback()
        if (this.rafId !== undefined) {
            cancelAnimationFrame(this.rafId)
            this.rafId = undefined
        }
    }
}